<!--suppress ALL -->
<template>
  <div class="modal" :class="{'is-active': isActive}">
    <div class="modal-background" @click="cancel('outside')"/>
    <div v-if="skipTransition" class="animation-content" :style="{ width: newWidth, maxWidth: newWidth }"
         :class="[{ 'modal-content': !hasModalCard }, position]">
      <slot></slot>
    </div>
    <transition v-else :name="animation"
                :enter-active-class="enterActiveClass"
                :leave-active-class="leaveActiveClass">
      <div v-if="isActive"
           class="animation-content"
           :class="[{ 'modal-content': !hasModalCard }, position]"
           :style="{ width: newWidth, maxWidth: newWidth }">
        <component
            v-if="component"
            v-bind="props"
            v-on="events"
            :is="component"
            @close="close"/>
        <div
            v-else-if="content"
            v-html="content"/>
        <slot v-else/>
      </div>
    </transition>
    <button type="button" v-if="showX" class="modal-close is-large" @click="cancel('x')"/>
  </div>
</template>

<script lang="ts">
  import {removeElement} from '../../../../util/Underscore'
  import {Component, Vue, Prop, Watch} from 'annotation';

  @Component
  export default class GModal extends Vue {

    @Prop({type: Boolean, default: false})
    public active: boolean;

    @Prop({type: [Object, Function]})
    public component: Object | Function;

    @Prop({type: String})
    public content: string;

    @Prop({type: Boolean, default: false})
    public programmatic: boolean;

    @Prop({type: Boolean, default: false})
    public skipTransition: boolean;

    @Prop({type: Object})
    public props: object;

    @Prop({type: Object})
    public events: object;

    @Prop({type: String, default: ''})
    public position: string;

    @Prop({type: [String, Number], default: 960})
    public width: string | number;

    @Prop({type: Boolean, default: false})
    public hasModalCard: boolean;

    @Prop({type: String, default: 'default-animation'})
    public animation: string;

    @Prop({type: String, default: ''})
    public enterActiveClass: string;

    @Prop({type: String, default: ''})
    public leaveActiveClass: string;

    @Prop({type: [Array, Boolean], default: () => ['escape', 'x', 'outside', 'button']})
    public canCancel: any;

    @Prop({
      type: Function, default: () => {
      }
    })
    public onCancel: Function;

    @Prop({type: String, default: 'clip'})
    public scroll: string;

    public savedScrollTop: any = null;

    public isActive: boolean = this.active || false;

    public get newWidth(): string {
      if (typeof this.width === 'number') return this.width + 'px';
      if (typeof this.width === 'string' && /^\d+$/.test(this.width)) return this.width + 'px';
      return this.width as string;
    }

    public get cancelOptions() {
      return typeof this.canCancel === 'boolean' ? this.canCancel ? ['escape', 'x', 'outside', 'button']
        : [] : this.canCancel;
    }

    public get showX() {
      return this.cancelOptions.indexOf('x') >= 0;
    }

    @Watch('active')
    onActive(value) {
      this.isActive = value;
    }

    @Watch('isActive')
    onIsActive() {
      this.handleScroll();
    }

    public handleScroll() {
      if (typeof window === 'undefined') return

      if (this.scroll === 'clip') {
        if (this.isActive) {
          document.documentElement.classList.add('is-clipped')
        } else {
          document.documentElement.classList.remove('is-clipped')
        }
        return
      }

      this.savedScrollTop = !this.savedScrollTop
        ? document.documentElement.scrollTop
        : this.savedScrollTop

      if (this.isActive) {
        document.body.classList.add('is-noscroll')
      } else {
        document.body.classList.remove('is-noscroll')
      }

      if (this.isActive) {
        document.body.style.top = `-${this.savedScrollTop}px`
        return
      }

      document.documentElement.scrollTop = this.savedScrollTop
      document.body.style.top = null
      this.savedScrollTop = null
    }

    /**
     * Close the Modal if canCancel and call the onCancel prop (function).
     */
    public cancel(method) {
      if (this.cancelOptions.indexOf(method) < 0) return

      this.onCancel.apply(null, arguments)
      this.close()
    }

    /**
     * Call the onCancel prop (function).
     * Emit events, and destroy modal if it's programmatic.
     */
    public close() {
      this.$emit('close')
      this.$emit('update:active', false)

      // Timeout for the animation complete before destroying
      if (this.programmatic) {
        this.isActive = false
        setTimeout(() => {
          this.$destroy()
          removeElement(this.$el)
        }, 150)
      }
    }

    /**
     * Keypress event that is bound to the document.
     */
    public keyPress(event) {
      // Esc key
      if (this.isActive && event.keyCode === 27) this.cancel('escape')
    }

    public created() {
      if (typeof window !== 'undefined') {
        document.addEventListener('keyup', this.keyPress)
      }
    }

    public beforeMount() {
      // Insert the Modal components in body tag
      // only if it's programmatic
      this.programmatic && document.body.appendChild(this.$el)
    }

    public mounted() {
      if (this.programmatic) this.isActive = true
      else if (this.isActive) this.handleScroll()
    }

    public beforeDestroy() {
      if (typeof window !== 'undefined') {
        document.removeEventListener('keyup', this.keyPress)
        // reset scroll
        document.documentElement.classList.remove('is-clipped')
        const savedScrollTop = !this.savedScrollTop
          ? document.documentElement.scrollTop
          : this.savedScrollTop
        document.body.classList.remove('is-noscroll')
        document.documentElement.scrollTop = savedScrollTop
        document.body.style.top = null
      }
    }
  }
</script>

<style lang="scss" scoped>
  .modal {
    .modal-content {
      &.top {
        top: 0;
      }

      &.bottom {
        bottom: 0;
      }

      &.left {
        left: 0;
      }

      &.right {
        right: 0;
      }

      &.top, &.bottom {
        position: absolute;
        margin: 0;
        min-width: 100vh;
      }

      &.left, &.right {
        position: absolute;
        margin: 0;
        min-height: 100vh;
      }

      &.right + .modal-close {
        left: 20px;
        right: 0;
      }

      &.top + .modal-close {
        bottom: 20px;
        top: 0;
      }
    }
  }
</style>
